import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AdvancedFilters = ({ onFiltersChange, isOpen, onToggle }) => {
  const [filters, setFilters] = useState({
    dateRange: {
      start: '2024-01-01',
      end: '2024-12-31'
    },
    dataSources: {
      bloomberg: true,
      reuters: true,
      ice: true,
      eex: false
    },
    anomalyDetection: {
      enabled: true,
      sensitivity: 'medium',
      threshold: 2.5
    },
    priceRange: {
      min: '',
      max: ''
    },
    volumeFilter: {
      enabled: false,
      minVolume: ''
    },
    marketConditions: []
  });

  const dataSourceOptions = [
    { id: 'bloomberg', label: 'Bloomberg Terminal', description: 'Real-time market data' },
    { id: 'reuters', label: 'Refinitiv Eikon', description: 'Financial data platform' },
    { id: 'ice', label: 'ICE Data Services', description: 'Exchange data feeds' },
    { id: 'eex', label: 'EEX Market Data', description: 'European energy exchange' }
  ];

  const sensitivityOptions = [
    { value: 'low', label: 'Low Sensitivity' },
    { value: 'medium', label: 'Medium Sensitivity' },
    { value: 'high', label: 'High Sensitivity' }
  ];

  const marketConditionOptions = [
    { value: 'high_volatility', label: 'High Volatility' },
    { value: 'low_liquidity', label: 'Low Liquidity' },
    { value: 'news_events', label: 'News Events' },
    { value: 'regulatory_changes', label: 'Regulatory Changes' }
  ];

  const handleFilterChange = (category, key, value) => {
    const newFilters = {
      ...filters,
      [category]: {
        ...filters?.[category],
        [key]: value
      }
    };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDataSourceToggle = (sourceId) => {
    const newSources = {
      ...filters?.dataSources,
      [sourceId]: !filters?.dataSources?.[sourceId]
    };
    const newFilters = { ...filters, dataSources: newSources };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleMarketConditionChange = (conditions) => {
    const newFilters = { ...filters, marketConditions: conditions };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const resetFilters = () => {
    const defaultFilters = {
      dateRange: { start: '2024-01-01', end: '2024-12-31' },
      dataSources: { bloomberg: true, reuters: true, ice: true, eex: false },
      anomalyDetection: { enabled: true, sensitivity: 'medium', threshold: 2.5 },
      priceRange: { min: '', max: '' },
      volumeFilter: { enabled: false, minVolume: '' },
      marketConditions: []
    };
    setFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  if (!isOpen) {
    return (
      <div className="mb-6">
        <Button
          variant="outline"
          onClick={onToggle}
          iconName="Filter"
          iconPosition="left"
          iconSize={16}
        >
          Advanced Filters
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Advanced Filters</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={resetFilters}
            iconName="RotateCcw"
            iconSize={14}
          >
            Reset
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onToggle}
            iconName="X"
            iconSize={14}
          >
            Close
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Date Range */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="Calendar" size={16} />
            Date Range
          </h4>
          <div className="space-y-3">
            <Input
              label="Start Date"
              type="date"
              value={filters?.dateRange?.start}
              onChange={(e) => handleFilterChange('dateRange', 'start', e?.target?.value)}
            />
            <Input
              label="End Date"
              type="date"
              value={filters?.dateRange?.end}
              onChange={(e) => handleFilterChange('dateRange', 'end', e?.target?.value)}
            />
          </div>
        </div>

        {/* Data Sources */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="Database" size={16} />
            Data Sources
          </h4>
          <div className="space-y-3">
            {dataSourceOptions?.map(source => (
              <Checkbox
                key={source?.id}
                label={source?.label}
                description={source?.description}
                checked={filters?.dataSources?.[source?.id]}
                onChange={() => handleDataSourceToggle(source?.id)}
              />
            ))}
          </div>
        </div>

        {/* Anomaly Detection */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="AlertTriangle" size={16} />
            Anomaly Detection
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Enable Anomaly Detection"
              checked={filters?.anomalyDetection?.enabled}
              onChange={(e) => handleFilterChange('anomalyDetection', 'enabled', e?.target?.checked)}
            />
            {filters?.anomalyDetection?.enabled && (
              <>
                <Select
                  label="Sensitivity Level"
                  options={sensitivityOptions}
                  value={filters?.anomalyDetection?.sensitivity}
                  onChange={(value) => handleFilterChange('anomalyDetection', 'sensitivity', value)}
                />
                <Input
                  label="Threshold (σ)"
                  type="number"
                  step="0.1"
                  value={filters?.anomalyDetection?.threshold}
                  onChange={(e) => handleFilterChange('anomalyDetection', 'threshold', parseFloat(e?.target?.value))}
                  description="Standard deviations from mean"
                />
              </>
            )}
          </div>
        </div>

        {/* Price Range */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="Euro" size={16} />
            Price Range
          </h4>
          <div className="space-y-3">
            <Input
              label="Minimum Price (€)"
              type="number"
              step="0.01"
              value={filters?.priceRange?.min}
              onChange={(e) => handleFilterChange('priceRange', 'min', e?.target?.value)}
              placeholder="0.00"
            />
            <Input
              label="Maximum Price (€)"
              type="number"
              step="0.01"
              value={filters?.priceRange?.max}
              onChange={(e) => handleFilterChange('priceRange', 'max', e?.target?.value)}
              placeholder="1000.00"
            />
          </div>
        </div>

        {/* Volume Filter */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="BarChart3" size={16} />
            Volume Filter
          </h4>
          <div className="space-y-3">
            <Checkbox
              label="Enable Volume Filter"
              checked={filters?.volumeFilter?.enabled}
              onChange={(e) => handleFilterChange('volumeFilter', 'enabled', e?.target?.checked)}
            />
            {filters?.volumeFilter?.enabled && (
              <Input
                label="Minimum Volume"
                type="number"
                value={filters?.volumeFilter?.minVolume}
                onChange={(e) => handleFilterChange('volumeFilter', 'minVolume', e?.target?.value)}
                placeholder="100000"
                description="Minimum trading volume"
              />
            )}
          </div>
        </div>

        {/* Market Conditions */}
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center gap-2">
            <Icon name="TrendingUp" size={16} />
            Market Conditions
          </h4>
          <Select
            label="Filter by Conditions"
            options={marketConditionOptions}
            value={filters?.marketConditions}
            onChange={handleMarketConditionChange}
            multiple
            searchable
            placeholder="Select conditions..."
          />
        </div>
      </div>
      {/* Apply Filters */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Filters will be applied automatically as you make changes
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            iconName="Save"
            iconSize={14}
          >
            Save Preset
          </Button>
          <Button
            variant="default"
            iconName="Filter"
            iconSize={14}
          >
            Apply Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedFilters;